import { OnlineContext } from 'app/providers/OnlineProvider';
import { SocketContext } from 'app/providers/SocketProvider';

import { useContext, useEffect, useState, useCallback } from 'react';

export const useOnlineDetection = ({
  onDisconnected = () => {},
  onReconnected = () => {},
}: {
  onDisconnected?: () => void;
  onReconnected?: () => void;
}) => {
  const { isOnline } = useContext(OnlineContext);
  const { socket } = useContext(SocketContext);

  const [wasDisconnected, setWasDisconnected] = useState(false);

  const onDisconnectedHandler = useCallback(() => {
    if (!wasDisconnected) {
      onDisconnected();
      setWasDisconnected(true);
    }
  }, [onDisconnected, wasDisconnected]);

  const onReconnectedHandler = useCallback(() => {
    if (wasDisconnected) {
      onReconnected();
      setWasDisconnected(false);
    }
  }, [onReconnected, wasDisconnected]);

  useEffect(() => {
    if (!socket) return;

    socket.on('disconnected', onDisconnectedHandler);

    socket.on('reconnected', onReconnectedHandler);

    return () => {
      socket.off('disconnected', onDisconnectedHandler);
      socket.off('reconnected', onReconnectedHandler);
    };
  }, [onDisconnectedHandler, onReconnectedHandler, socket, wasDisconnected]);

  useEffect(() => {
    if (!isOnline && !wasDisconnected) {
      onDisconnectedHandler();
      return;
    }

    if (isOnline && wasDisconnected) {
      onReconnectedHandler();
      return;
    }
  }, [isOnline, onDisconnectedHandler, onReconnectedHandler, wasDisconnected]);

  return { isOnline };
};
